/**
 * Typed API client for the Brain server.
 * All shapes match the route files in apps/server/src/routes/api/.
 * Credentials are included so the session cookie rides along.
 */

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? res.statusText, body);
  }
  return body as T;
}

// Multipart form upload (no Content-Type header — browser sets boundary).
async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, (body as { error?: string })?.error ?? res.statusText, body);
  }
  return body as T;
}

export { ApiError };

// ---------------------------------------------------------------------------
// Types — mirrored from route files
// ---------------------------------------------------------------------------

export interface WorkspaceSettings {
  timezone: string | null;
  onboardingState: OnboardingState | null;
}

export interface Workspace {
  id: string;
  name: string;
  /**
   * Org-scoped MCP URLs (per-org MCP URLs plan D1): `mcpEndpoint({ slug })`
   * resolves the workspace from the URL instead of the credential. Already
   * returned by `getWorkspace` server-side — no server change for this field.
   */
  slug: string;
  /**
   * Plain family slugs (`whatsapp`, `slack`, `signal`, `granola`) this
   * workspace has at least one `sources` row for — the server-side source of
   * truth for AC22's scoped-URL gating (packages/db `getWorkspace`). Do NOT
   * infer this client-side: `whatsapp_export`/`signal_export` land via
   * imports with no connector account behind them, so a client-side guess
   * from account-list endpoints alone would silently miss those workspaces.
   */
  connectedFamilies: string[];
  settings: WorkspaceSettings;
  /**
   * Domain auto-join (plan 2026-07-24): the claimed email domain, normalized
   * lowercase, or null when disabled. Writable only via workspaceApi.setDomain
   * — stock org endpoints reject it (`input: false` server-side).
   */
  autoJoinDomain: string | null;
}

/** State machine from plan §5.1. */
export type OnboardingState =
  | 'not_started'
  | 'profile_created'
  | 'number_ordered'
  | 'number_active'
  | 'account_connected'
  | 'group_ready';

export interface Group {
  id: string;
  externalId: string | null;
  title: string;
  ingestionEnabled: boolean;
  lastActivityAt: string | null;
  staleAt: string | null;
  /** Which connector backs this shared group — drives history-import routing. */
  connector: 'whatsapp' | 'signal' | 'slack' | 'other';
  /**
   * Whether Brain is still in the group. `null` when there is no routing row to
   * speak for it — treat as "unknown", never as a problem.
   */
  membership: 'joined' | 'left' | null;
  groupJid: string | null;
  canRejoin: boolean;
}

export interface ApiKey {
  id: string;
  name: string | null;
  start: string | null;
  prefix: string | null;
  enabled: boolean;
  createdAt: string;
  lastRequest: string | null;
}

export interface CreatedApiKey {
  apiKey: Pick<ApiKey, 'id' | 'name' | 'start' | 'prefix' | 'createdAt'>;
  /** Plaintext key — shown once, never retrievable again. */
  key: string;
}

export interface ImportBatch {
  id: string;
  filename: string;
  sizeBytes: number;
  declaredTimezone: string;
  status: 'parsing' | 'indexing' | 'done' | 'failed';
  counts: {
    parsed: number;
    imported: number;
    deduped: number;
    system_skipped: number;
    media_staged?: number;
    media_skipped?: number;
    /** Voice notes indexed with a transcript reused from the export manifest. */
    media_transcribed?: number;
  } | null;
  /**
   * ONE of the batch's collections. A Signal backup produces many, and this
   * records only the first — use `selection.chats` for the full set.
   */
  collectionId: string | null;
  /**
   * Written only by the Signal import's select step, and never cleared, so it
   * doubles as the connector discriminator: non-null ⇔ a Signal batch that has
   * been selected. (A Signal batch still at the manifest stage is null here, but
   * also has no collections, so nothing can mis-attribute it.)
   */
  selection: { chats: SignalImportCollection[] } | null;
  error: string | null;
  createdAt: string;
}

export interface AmbiguityInterpretation {
  dateOrder: 'dmy' | 'mdy';
  sample: string;
}

export interface ImportAmbiguityError {
  error: 'ambiguous_date_order';
  batchId: string;
  interpretations: AmbiguityInterpretation[];
  samples: string[];
  message: string;
}

// ---------------------------------------------------------------------------
// Workspace API
// ---------------------------------------------------------------------------

export const workspaceApi = {
  get: () => apiFetch<{ workspace: Workspace }>('/api/workspaces'),
  patch: (body: { name?: string; timezone?: string }) =>
    apiFetch<{ workspace: Workspace }>('/api/workspaces', {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  /**
   * Domain auto-join: owner claims their verified-email domain, or clears it
   * with null. ALL validation is server-side — error codes: invalid_domain,
   * public_domain_blocked, email_unverified, domain_mismatch (400),
   * session_required / owner_required (403), domain_taken (409).
   */
  setDomain: (domain: string | null) =>
    apiFetch<{ workspace: Workspace }>('/api/workspaces/domain', {
      method: 'PUT',
      body: JSON.stringify({ domain }),
    }),
  /**
   * Danger zone: delete the active workspace (P0-2 fix).
   * confirmName must exactly match the current workspace name — enforced both
   * client-side (UX gate) and server-side (confirmation_mismatch 400).
   * The server enqueues the index purge BEFORE deleting the Postgres org row,
   * so the search namespace is never left orphaned on a mid-delete crash.
   */
  delete: (confirmName: string) =>
    apiFetch<{ deleted: boolean; workspaceId: string }>('/api/workspaces', {
      method: 'DELETE',
      body: JSON.stringify({ confirmName }),
    }),
};

// ---------------------------------------------------------------------------
// WAHA connector API (live groups — link by invite link)
// ---------------------------------------------------------------------------

export interface WahaGroupPreview {
  groupJid: string;
  name: string;
  participantCount: number;
  joinApprovalRequired: boolean;
  /** Already linked to a DIFFERENT workspace — cannot link here. */
  conflict: boolean;
  /** Already linked to THIS workspace. */
  alreadyLinked: boolean;
}

export interface WahaLinkResult {
  groupJid: string;
  name: string;
  status: string;
  pendingApproval: boolean;
}

export interface LinkedGroup {
  groupJid: string;
  collectionId: string;
  /** The group's name. Null when the collection carries no title. */
  name: string | null;
  status:
    | 'previewing'
    | 'pending_approval'
    | 'joined'
    | 'announced'
    | 'active'
    | 'removed'
    | 'conflict';
  joinedAt: string | null;
  /** When Brain stopped being in the group — set only for `removed`. */
  removedAt: string | null;
  /** False when no invite code was stored, so rejoin needs a fresh link. */
  canRejoin: boolean;
}

export interface WahaRejoinResult {
  groupJid: string;
  name?: string;
  status: LinkedGroup['status'];
  announced?: boolean;
  pendingApproval?: boolean;
  alreadyActive?: boolean;
}

export const wahaApi = {
  /** Preview a group from its invite link — no side effects (join-info). */
  preview: (inviteLink: string) =>
    apiFetch<WahaGroupPreview>('/api/connectors/waha/preview', {
      method: 'POST',
      body: JSON.stringify({ inviteLink }),
    }),
  /** Join the group, post the consent announce, bind it to this workspace. */
  link: (inviteLink: string) =>
    apiFetch<WahaLinkResult>('/api/connectors/waha/link', {
      method: 'POST',
      body: JSON.stringify({ inviteLink }),
    }),
  groups: () => apiFetch<LinkedGroup[]>('/api/connectors/waha/groups'),
  /** Rejoin a group Brain is out of, using the invite code stored at link time. */
  rejoin: (groupJid: string) =>
    apiFetch<WahaRejoinResult>(
      `/api/connectors/waha/groups/${encodeURIComponent(groupJid)}/rejoin`,
      { method: 'POST' },
    ),
  unlink: (groupJid: string) =>
    apiFetch<{ removed: boolean }>(`/api/connectors/waha/groups/${encodeURIComponent(groupJid)}`, {
      method: 'DELETE',
    }),
};

// ---------------------------------------------------------------------------
// WAHA BYO session API (connect your own number — QR pairing)
// ---------------------------------------------------------------------------

export type WahaSessionStatus =
  | 'pending_qr'
  | 'scanning'
  | 'working'
  | 'failed'
  | 'logged_out'
  | 'error';

export interface WahaSession {
  id: string;
  kind: 'shared' | 'byo';
  status: WahaSessionStatus;
  visibility: 'workspace' | 'private';
  phoneE164: string | null;
  createdAt: string;
}

export interface WahaChat {
  chatId: string;
  name: string | null;
  kind: 'group' | 'dm';
  picture: string | null;
  lastMessageAt: number | null;
}

export interface ChatSelection {
  chatId: string;
  visibility?: 'workspace' | 'private';
}

/** An indexed chat under a BYO session (the /sources management view). */
export interface WahaSelectedChat {
  chatId: string;
  kind: 'group' | 'dm';
  title: string;
  collectionId: string;
  visibility: 'workspace' | 'private';
  ingestionEnabled: boolean;
  seedStatus: string | null;
}

export const wahaSessionApi = {
  /** Create + start a per-user WAHA session; returns its id (status pending_qr). */
  create: (visibility?: 'workspace' | 'private') =>
    apiFetch<{ sessionId: string; status: WahaSessionStatus }>('/api/connectors/waha/sessions', {
      method: 'POST',
      body: JSON.stringify({ visibility }),
    }),
  list: () => apiFetch<WahaSession[]>('/api/connectors/waha/sessions'),
  status: (id: string) => apiFetch<WahaSession>(`/api/connectors/waha/sessions/${id}`),
  /** QR to scan (re-fetch each poll — it rotates on SCAN_QR_CODE). */
  qr: (id: string) =>
    apiFetch<{ qr: string; status: WahaSessionStatus }>(`/api/connectors/waha/sessions/${id}/qr`),
  pair: (id: string, phoneNumber: string) =>
    apiFetch<{ code: string }>(`/api/connectors/waha/sessions/${id}/pair`, {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    }),
  disconnect: (id: string) =>
    apiFetch<{ disconnected: boolean }>(`/api/connectors/waha/sessions/${id}`, {
      method: 'DELETE',
    }),
  /** The chat picker's list (groups + DMs). */
  chats: (id: string) => apiFetch<WahaChat[]>(`/api/connectors/waha/sessions/${id}/chats`),
  /** Pick chats to index — creates per-session collections + waha_chats rows. */
  selectChats: (id: string, chats: ChatSelection[]) =>
    apiFetch<{ selected: number }>(`/api/connectors/waha/sessions/${id}/chats/select`, {
      method: 'POST',
      body: JSON.stringify({ chats }),
    }),
  /** Indexed chats under a session, with collection state (for /sources). */
  selectedChats: (id: string) =>
    apiFetch<WahaSelectedChat[]>(`/api/connectors/waha/sessions/${id}/selected`),
};

// ---------------------------------------------------------------------------
// Signal BYO account API (link your own Signal — QR device-link)
//
// Mirrors apps/server/src/routes/api/signal-accounts.ts. Every :id route is
// OWNER-scoped and answers 404 to a non-owner, so treat a 404 as "not yours /
// gone", never as a transient error worth retrying.
// ---------------------------------------------------------------------------

export type SignalAccountStatus = 'pending_link' | 'linking' | 'working' | 'unlinked' | 'error';

/**
 * Why a link hasn't completed yet. `container_restart_required` is the important
 * one: signal-cli builds its account map at daemon startup, so a device linked
 * after the container booted stays invisible until it restarts — the scan really
 * did succeed, and the UI must say so instead of spinning forever.
 */
export type SignalLinkHint =
  | 'container_restart_required'
  | 'ambiguous_link'
  | 'signal_unavailable'
  | null;

export interface SignalAccount {
  id: string;
  kind: 'byo';
  status: SignalAccountStatus;
  visibility: 'workspace' | 'private';
  e164: string | null;
  aci: string | null;
  deviceName: string | null;
  createdAt: string;
}

/** The poll target's response — an account plus why it isn't linked yet. */
export interface SignalAccountState extends SignalAccount {
  linkHint: SignalLinkHint;
}

/** One selectable chat from the picker's list. */
export interface SignalChat {
  chatKey: string;
  name: string | null;
  kind: 'group' | 'dm';
  memberCount?: number;
}

/** An indexed chat under an account (the /sources management view). */
export interface SignalSelectedChat {
  chatKey: string;
  kind: 'group' | 'dm';
  title: string;
  collectionId: string;
  visibility: 'workspace' | 'private';
  ingestionEnabled: boolean;
}

export interface SignalSelectResult {
  selected: number;
  chats: Array<{
    chatKey: string;
    collectionId: string;
    kind: 'group' | 'dm';
    visibility: 'workspace' | 'private';
  }>;
  /** Requested keys the account can no longer see — surface, don't swallow. */
  skipped: string[];
}

export interface SignalSelectRequest {
  chatKeys?: string[];
  all?: boolean;
  /** Applies to GROUPS ONLY — DMs are always private, server-side. */
  visibility?: 'workspace' | 'private';
}

export const signalAccountApi = {
  /** Create the row a QR link binds to. 429 ⇒ per-workspace account ceiling hit. */
  create: (visibility?: 'workspace' | 'private') =>
    apiFetch<{ accountId: string; status: SignalAccountStatus }>(
      '/api/connectors/signal/accounts',
      {
        method: 'POST',
        body: JSON.stringify({ visibility }),
      },
    ),
  list: () => apiFetch<SignalAccount[]>('/api/connectors/signal/accounts'),
  /** Poll target: claims the linked number once it appears on the container. */
  status: (id: string) => apiFetch<SignalAccountState>(`/api/connectors/signal/accounts/${id}`),
  /**
   * The QR to scan, as a ready-to-render data URL. SINGLE-USE — fetching it opens
   * the link window, so fetch once and re-fetch only on an explicit retry. Poll
   * {@link status} for progress, never this.
   */
  qr: (id: string) =>
    apiFetch<{ qr: string; status: SignalAccountStatus; deviceName: string }>(
      `/api/connectors/signal/accounts/${id}/qr`,
    ),
  /** The picker's list (groups + known DM contacts). 409 ⇒ not linked yet. */
  chats: (id: string) => apiFetch<SignalChat[]>(`/api/connectors/signal/accounts/${id}/chats`),
  /**
   * Pick chats to index. This is the COMPLETE desired set, not an append: any
   * previously-selected chat omitted here stops ingesting (its collection and
   * indexed data remain). No history backfill is enqueued — Signal linked devices
   * have no history to fetch.
   */
  selectChats: (id: string, body: SignalSelectRequest) =>
    apiFetch<SignalSelectResult>(`/api/connectors/signal/accounts/${id}/chats/select`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  selectedChats: (id: string) =>
    apiFetch<SignalSelectedChat[]>(`/api/connectors/signal/accounts/${id}/selected`),
  /** Stops new messages. Indexed data stays searchable — this is not a delete. */
  disconnect: (id: string) =>
    apiFetch<{ disconnected: boolean }>(`/api/connectors/signal/accounts/${id}`, {
      method: 'DELETE',
    }),
};

// ---------------------------------------------------------------------------
// Signal SHARED-NUMBER connector API (add Brain's number to a group)
//
// Mirrors apps/server/src/routes/api/signal.ts and parallels `wahaApi` above,
// with one flow difference that shapes the whole UI: WhatsApp JOINS a group from
// its invite link, Signal CANNOT. The customer adds Brain's number to the group
// on their phone first; the link is only a selector proving they have access.
// So `not_a_member` (404) is the EXPECTED state before they do that — a step in
// the flow to poll on, not a failure to report.
// ---------------------------------------------------------------------------

export interface SignalSharedConfig {
  /** False ⇒ SIGNAL_SHARED_NUMBER/SIGNAL_URL unset; the flow must not be offered. */
  configured: boolean;
  /** The number to add to the group. Null exactly when `configured` is false. */
  sharedNumber: string | null;
}

export interface SignalGroupPreview {
  chatKey: string;
  name: string;
  memberCount: number;
  /** Already bound to a DIFFERENT workspace — cannot bind here. */
  conflict: boolean;
  /** Already bound to THIS workspace. */
  alreadyLinked: boolean;
  /**
   * Brain has been added to the group but is still an unaccepted invite, so it
   * receives nothing yet. Binding accepts the invite; nothing else is needed from
   * the customer unless the group requires admin approval of new members.
   */
  pending: boolean;
}

export interface SignalBindResult {
  chatKey: string;
  collectionId: string;
  name: string;
  status: string;
  /** False ⇒ bound and ingesting, but the consent message didn't send. */
  announced: boolean;
}

export interface SignalLinkedGroup {
  chatKey: string;
  collectionId: string;
  name: string | null;
  status: string;
  createdAt: string;
}

export const signalSharedApi = {
  /** Whether the shared flow is available, and the number to add to the group. */
  config: () => apiFetch<SignalSharedConfig>('/api/connectors/signal/config'),
  /**
   * Identify the group behind a link — no side effects. Throws ApiError 404
   * (`not_a_member`) until Brain has been added to the group, which is the
   * normal pre-add state, not an error worth surfacing as one.
   */
  preview: (inviteLink: string) =>
    apiFetch<SignalGroupPreview>('/api/connectors/signal/preview', {
      method: 'POST',
      body: JSON.stringify({ inviteLink }),
    }),
  /** Bind the group to this workspace, then post the consent announce. */
  bind: (inviteLink: string) =>
    apiFetch<SignalBindResult>('/api/connectors/signal/bind', {
      method: 'POST',
      body: JSON.stringify({ inviteLink }),
    }),
  groups: () => apiFetch<SignalLinkedGroup[]>('/api/connectors/signal/groups'),
  /** Brain leaves the group and stops ingesting. Indexed data stays searchable. */
  unlink: (chatKey: string) =>
    apiFetch<{ removed: boolean }>(`/api/connectors/signal/groups/${encodeURIComponent(chatKey)}`, {
      method: 'DELETE',
    }),
};

// ---------------------------------------------------------------------------
// Signal export import (two-step: upload→manifest, then select→import)
//
// Mirrors apps/server/src/routes/api/imports.ts. Unlike the WhatsApp import
// (one file = one chat), one Signal backup carries EVERY chat, so the user picks
// which ones to import from a manifest.
// ---------------------------------------------------------------------------

export interface SignalManifestChat {
  chatKey: string;
  title: string;
  kind: 'group' | 'dm' | 'self';
  messageCount: number;
  /** ISO-8601, or null when the chat has no resolvable timestamps. */
  firstAt: string | null;
  lastAt: string | null;
}

export interface SignalParseSummary {
  frames: number;
  unparseableLines: number;
  recipients: number;
  /** ALL chats in the backup — larger than manifest.length, which lists only
   *  chats with at least one indexable message. */
  chats: number;
  chatItems: number;
  messages: number;
  withMedia: number;
  edited: number;
  backupTimeMs: number;
  skipped: Record<string, number>;
}

export interface SignalUploadResult {
  batchId: string;
  backupId: string;
  status: string;
  manifest: SignalManifestChat[];
  summary: SignalParseSummary;
}

export interface SignalImportSelection {
  chatKey: string;
  /** Omit to take the server default: group→workspace, dm/self→private. */
  visibility?: 'workspace' | 'private';
}

export interface SignalImportCollection {
  chatKey: string;
  collectionId: string;
  title: string;
  kind: 'group' | 'dm' | 'self';
  visibility: 'workspace' | 'private';
}

/**
 * Step 2's response — **202 Accepted**, not 200. The per-message work runs as a
 * background job, so there are NO counts here; collection creation stays
 * synchronous so the client gets real `collectionId`s immediately.
 *
 * `status` is the literal `'importing'`, which is a RESPONSE-ONLY value — the
 * `import_batches.status` column has no such state (its enum is
 * parsing|indexing|done|failed). Poll `importsApi.get(batchId)` for progress:
 * the row sits at `parsing` while the job runs, flips to `indexing` when the
 * messages are in (with counts), then `done` once the closer finishes.
 */
export interface SignalImportResult {
  batchId: string;
  status: 'importing';
  collections: SignalImportCollection[];
}

export const signalImportApi = {
  /**
   * Step 1 — upload a Signal native backup (main.jsonl, or a .zip containing
   * it) and get back the chat manifest. No timezone: Signal timestamps are
   * absolute epoch ms.
   */
  upload: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiUpload<SignalUploadResult>('/api/imports/signal', form);
  },
  /**
   * Step 2 — import the chosen chats. Returns 202: the messages land via a
   * background job. `410 manifest_expired` means the stored parse is gone and
   * the file must be uploaded again.
   *
   * Collections are keyed on the importing user, not the backup file, so
   * importing a NEWER backup merges into the same collections and dedupes
   * rather than creating a duplicate set.
   */
  selectChats: (batchId: string, chats: SignalImportSelection[]) =>
    apiFetch<SignalImportResult>(`/api/imports/signal/${batchId}/select`, {
      method: 'POST',
      body: JSON.stringify({ chats }),
    }),
};

// ---------------------------------------------------------------------------
// Slack connector API (OAuth — connect your Slack account)
//
// Mirrors apps/server/src/routes/api/slack-accounts.ts. Owner-scoped like the
// Signal BYO API: a :id route answers 404 to a non-owner, so treat a 404 as
// "not yours / gone", never a transient error worth retrying. Unlike WAHA and
// Signal there is no create / QR / chat-picker flow: connecting an account IS
// indexing it — every conversation the user is in is indexed automatically, and
// the OAuth callback does all the binding. This client only starts the redirect,
// lists the caller's own accounts, and disconnects.
// ---------------------------------------------------------------------------

export type SlackAccountStatus = 'connecting' | 'working' | 'rate_limited' | 'revoked' | 'error';

export interface SlackAccount {
  id: string;
  slackTeamId: string;
  slackTeamName: string | null;
  slackUserName: string | null;
  status: SlackAccountStatus;
  visibility: 'workspace' | 'private';
  createdAt: string;
}

export const slackAccountApi = {
  /**
   * Begin OAuth — returns Slack's authorize URL to hard-navigate to. 503 ⇒ Slack
   * unconfigured; 429 ⇒ per-workspace account ceiling hit (`slack_account_limit_reached`).
   */
  oauthStart: () => apiFetch<{ authorizeUrl: string }>('/api/connectors/slack/oauth/start'),
  list: () => apiFetch<SlackAccount[]>('/api/connectors/slack/accounts'),
  /**
   * Revokes the Slack token and RETAINS everything already indexed — undoing this
   * requires a fresh OAuth (matches WAHA/Signal disconnect semantics).
   */
  disconnect: (id: string) =>
    apiFetch<{ disconnected: boolean }>(`/api/connectors/slack/accounts/${id}`, {
      method: 'DELETE',
    }),
};

// ---------------------------------------------------------------------------
// Granola connector API (paste-key — no OAuth, plan §1.1)
//
// Mirrors apps/server/src/routes/api/granola.ts. Owner-scoped like Signal/WAHA
// BYO — a :id route answers 404 to a non-owner. Unlike every other connector,
// "connecting" is a pasted key validated live server-side rather than a
// redirect: there is no oauthStart here, and `connect` both creates the
// account AND returns the raw R11 signal (visibleNoteCount/hasMore) this
// client needs in order to warn on a likely workspace key BEFORE seeding
// starts. The server deliberately hands back facts only — deciding whether
// to warn, and how, is a UI concern (see StepGranolaConnect).
// ---------------------------------------------------------------------------

export type GranolaAccountStatus =
  | 'connecting'
  | 'active'
  | 'invalid_key'
  | 'rate_limited'
  | 'error'
  | 'disconnected';

/** Wizard/background seed progress, persisted server-side as `seed_state`. */
export interface GranolaSeedState {
  status: 'running' | 'done' | 'failed';
  fetched: number;
  cap: number;
  cursor: string | null;
  /** The connect-time probe result — feeds the R11 workspace-key warning. */
  visibleNoteCount: number | null;
  startedAt: string;
  /**
   * R14: best-effort count of notes the seed did NOT reach because it hit
   * GRANOLA_SEED_WINDOW_DAYS before GRANOLA_SEED_MAX_NOTES (measured: a
   * 365-day window silently captured only 63% of a 938-note corpus). `0`
   * means the window caught everything — nothing to say. `null`/`undefined`
   * is a genuine UNKNOWN (never truncated by the window, or visibleNoteCount
   * itself was never captured) — NEVER render this as "0 dropped", that
   * would assert something the server doesn't actually know.
   */
  droppedByWindow?: number | null;
  /**
   * Notes Granola's own API answers 5xx for, every time — measured in
   * production at two notes in 938. Skipped so the walk could finish, and
   * re-attempted by every sync tick, so this is a "waiting on Granola" list,
   * not a permanent loss.
   *
   * DELIBERATELY NOT RENDERED. There is no user action that resolves these, so
   * showing them would be a warning with no move attached; the retry is already
   * automatic. Kept on the type because the server does send it, and it is the
   * shape anything operator-facing would read. If you find yourself wanting to
   * display it, the question to answer first is "what should the reader DO?".
   */
  skippedNoteIds?: string[];
}

export interface GranolaAccount {
  id: string;
  label: string | null;
  keyLast4: string;
  status: GranolaAccountStatus;
  granolaOwnerEmail: string | null;
  lastSyncedAt: string | null;
  lastError: string | null;
  seedState: GranolaSeedState | null;
  createdAt: string;
}

/**
 * `hasMore:false` means `visibleNoteCount` is the EXACT total (the connect
 * probe pages at size 30) — a small exact count is the R11 workspace-key
 * signature (measured: a real workspace key saw 12 notes total; a personal
 * key on the same account saw 938). `hasMore:true` means ">30, exact
 * unknown" and rules the warning out. Connect always succeeds either way —
 * a low count is a warning, never a rejection.
 */
export interface GranolaConnectResult {
  id: string;
  status: 'connecting';
  visibleNoteCount: number;
  hasMore: boolean;
}

export const granolaAccountApi = {
  /**
   * 400 invalid_key_format (shape-checked before any network call) · 401
   * invalid_key (body.message names the plan-gate cause) · 429 rate_limited ·
   * 502 connect_failed · 503 credentials_key_not_configured.
   */
  connect: (apiKey: string, label?: string) =>
    apiFetch<GranolaConnectResult>('/api/connectors/granola/connect', {
      method: 'POST',
      body: JSON.stringify({ apiKey, ...(label ? { label } : {}) }),
    }),
  /** The caller's OWN accounts only — mirrors GET /slack/accounts. */
  list: () => apiFetch<GranolaAccount[]>('/api/connectors/granola/status'),
  /** No :id — resyncs every account the caller owns (escape hatch for R2). */
  resyncAll: () =>
    apiFetch<{ resynced: string[] }>('/api/connectors/granola/resync', { method: 'POST' }),
  /**
   * Removes Brain's copy of the key; everything already indexed stays
   * searchable. Granola has no revoke API — `message` carries that honest
   * boundary verbatim, straight from the server (doctrine 2.5).
   */
  disconnect: (id: string) =>
    apiFetch<{ disconnected: boolean; message: string }>(`/api/connectors/granola/${id}`, {
      method: 'DELETE',
    }),
  /**
   * Enqueues a purge of every note/transcript under the WORKSPACE's single
   * Granola source (plan §4.2 — one source, N accounts) — not just the notes
   * this one account contributed. Callers must word confirmation copy
   * accordingly; see GranolaConnectedAccounts.
   */
  deleteData: (id: string) =>
    apiFetch<{ purging: boolean }>(`/api/connectors/granola/${id}/data`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// Granola meeting collections — per-account, paginated (plan §7.4/§5.7).
//
// GET /api/connectors/granola/:id/collections (task #13). Matches the house
// keyset-cursor convention already used by connector-collections.ts's
// message pagination: `{items, nextCursor}`, opaque cursor, `limit` clamped
// server-side, scoped to this account's owner under the workspace's single
// shared Granola source. A note stored with title:null (plan §4.4) already
// arrives here as 'Untitled meeting' — the server applies that fallback at
// the response edge (§5.8), so `title` is never empty. The ingestion toggle
// needs no dedicated endpoint: it reuses the existing generic
// `connectorApi.patchCollection`, since Granola notes are plain `collections`
// rows (type:'meeting').
// ---------------------------------------------------------------------------

export interface GranolaMeeting {
  id: string;
  title: string;
  lastActivityAt: string | null;
  ingestionEnabled: boolean;
}

export interface GranolaMeetingsPage {
  items: GranolaMeeting[];
  nextCursor: string | null;
}

export const granolaMeetingsApi = {
  list: (accountId: string, opts?: { limit?: number; cursor?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString();
    return apiFetch<GranolaMeetingsPage>(
      `/api/connectors/granola/${encodeURIComponent(accountId)}/collections${qs ? `?${qs}` : ''}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Connector API (collections)
// ---------------------------------------------------------------------------

export const connectorApi = {
  listGroups: () => apiFetch<Group[]>('/api/connectors/whatsapp/groups'),
  patchCollection: (id: string, ingestionEnabled: boolean) =>
    apiFetch<{ id: string; ingestion_enabled: boolean }>(`/api/connectors/collections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ingestion_enabled: ingestionEnabled }),
    }),
  setCollectionVisibility: (id: string, visibility: 'workspace' | 'private') =>
    apiFetch<{ id: string; visibility: 'workspace' | 'private' }>(
      `/api/connectors/collections/${id}`,
      { method: 'PATCH', body: JSON.stringify({ visibility }) },
    ),
};

// ---------------------------------------------------------------------------
// Imports API
// ---------------------------------------------------------------------------

export const importsApi = {
  upload: async (
    file: File,
    timezone: string,
    collectionTitle: string,
    dateOrder?: 'dmy' | 'mdy',
    /** When supplied, imports into an existing collection; collectionTitle is ignored. */
    collectionId?: string,
  ) => {
    const form = new FormData();
    form.append('file', file);
    form.append('timezone', timezone);
    if (collectionId) {
      form.append('collectionId', collectionId);
    } else {
      form.append('collectionTitle', collectionTitle);
    }
    if (dateOrder) form.append('dateOrder', dateOrder);
    // 422 is a valid structured response — we need to handle it without throwing.
    const res = await fetch('/api/imports', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    const body = await res.json().catch(() => null);
    // 422 has two shapes: an ambiguous-date response (carries `interpretations`)
    // and hard rejections like `unrecognized_format` (no interpretations). Only
    // the former is a structured "ask the user" result; the latter is an error.
    if (
      res.status === 422 &&
      Array.isArray((body as ImportAmbiguityError | null)?.interpretations)
    ) {
      return { ambiguity: body as ImportAmbiguityError };
    }
    if (!res.ok)
      throw new ApiError(
        res.status,
        (body as { message?: string; error?: string })?.message ??
          (body as { error?: string })?.error ??
          res.statusText,
        body,
      );
    return {
      result: body as {
        batchId: string;
        collectionId: string;
        counts: ImportBatch['counts'];
        status: string;
      },
    };
  },
  list: () => apiFetch<{ batches: ImportBatch[] }>('/api/imports'),
  get: (id: string) => apiFetch<ImportBatch>(`/api/imports/${id}`),
};

// ---------------------------------------------------------------------------
// MCP source families (per-org MCP URLs plan D4/AC22)
// ---------------------------------------------------------------------------

/**
 * The four connector families a source-scoped MCP endpoint can name. Plain
 * names by design (D4) — a display-only client mirror of the server's family
 * registry (apps/server/src/mcp/families.ts). There are only four, so drift
 * between the two lists is cheap to notice (a wrong URL on manual test) and
 * cheap to fix.
 */
export type McpFamilySlug = 'whatsapp' | 'slack' | 'signal' | 'granola';

export const MCP_FAMILIES: ReadonlyArray<{ slug: McpFamilySlug; label: string }> = [
  { slug: 'whatsapp', label: 'WhatsApp' },
  { slug: 'slack', label: 'Slack' },
  { slug: 'signal', label: 'Signal' },
  { slug: 'granola', label: 'Granola' },
];

// ---------------------------------------------------------------------------
// MCP keys API
// ---------------------------------------------------------------------------

export const mcpKeysApi = {
  list: () => apiFetch<{ keys: ApiKey[] }>('/api/mcp-keys'),
  /** A key reads the whole workspace; the only input is a human label. */
  create: (input: { name: string }) =>
    apiFetch<CreatedApiKey>('/api/mcp-keys', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  revoke: (id: string) =>
    apiFetch<{ revoked: boolean }>(`/api/mcp-keys/${id}`, { method: 'DELETE' }),
};

// ---------------------------------------------------------------------------
// OAuth consent API (apps/server/src/routes/api/oauth-consent.ts + better-auth)
// ---------------------------------------------------------------------------

export interface OAuthConsentDetails {
  clientId: string;
  clientName: string;
  clientUri: string | null;
  logoUri: string | null;
  scopes: string[];
  /** Null when the client has no usable redirect URI on record. */
  redirectUriHost: string | null;
  isLoopbackOnly: boolean;
  /**
   * Workspace this grant will read, resolved server-side from the authorize
   * request's `resource` param (membership-checked) and falling back to the
   * session's active workspace. Null when neither resolved — render nothing.
   */
  workspaceName: string | null;
}

export interface OAuthConsentDecision {
  /** better-auth hands back the client's redirect target to follow on approve. */
  redirectURI?: string;
  redirect_uri?: string;
}

export const oauthConsentApi = {
  /** Session-only; uniform 404 for unknown, expired, or foreign codes (plan R5). */
  details: (code: string) =>
    apiFetch<OAuthConsentDetails>(`/api/oauth/consent-details?code=${encodeURIComponent(code)}`),
  decide: (consentCode: string, accept: boolean) =>
    apiFetch<OAuthConsentDecision>('/api/auth/oauth2/consent', {
      method: 'POST',
      body: JSON.stringify({ consent_code: consentCode, accept }),
    }),
};

// ---------------------------------------------------------------------------
// Collection detail + messages API
// ---------------------------------------------------------------------------

/** Pinned contract: GET /api/connectors/collections/:collectionId */
export interface CollectionDetail {
  id: string;
  title: string;
  type: string;
  ingestionEnabled: boolean;
  visibility: 'workspace' | 'private';
  lastActivityAt: string | null;
  staleAt: string | null;
  messageCount: number;
}

/**
 * Media annotations produced by the media:process sidecar — a voice-note
 * transcript or an image description, plus why one is missing. The underlying
 * `ref` is server-side only and never present here.
 */
export interface MessageMedia {
  type: string;
  transcript?: string;
  description?: string;
  error?: string;
  processed: boolean;
}

export interface CollectionMessage {
  id: string;
  senderName: string;
  sentAt: string;
  text: string;
  hasMedia: boolean;
  media: MessageMedia | null;
}

export interface CollectionMessagesPage {
  items: CollectionMessage[];
  nextCursor: string | null;
}

export const collectionApi = {
  get: (collectionId: string) =>
    apiFetch<CollectionDetail>(`/api/connectors/collections/${encodeURIComponent(collectionId)}`),
  listMessages: (collectionId: string, opts?: { limit?: number; before?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.before) params.set('before', opts.before);
    const qs = params.toString();
    return apiFetch<CollectionMessagesPage>(
      `/api/connectors/collections/${encodeURIComponent(collectionId)}/messages${qs ? `?${qs}` : ''}`,
    );
  },
};

// ---------------------------------------------------------------------------
// Source account detail + messages API
// ---------------------------------------------------------------------------

export type ConnectorType = 'slack' | 'signal' | 'whatsapp';

export type NormalizedConnectionStatus =
  | 'connected'
  | 'degraded'
  | 'error'
  | 'disconnected'
  | 'pending';

export interface SourceAccount {
  connector: ConnectorType;
  accountId: string;
  displayName: string;
  kind: 'byo' | 'shared' | null;
  connection: {
    status: NormalizedConnectionStatus;
    rawStatus: string;
    lastSeenAt: string | null;
    lastStatusAt: string | null;
  };
  data: {
    lastActivityAt: string | null;
    collectionCount: number;
    messageCount: number;
  };
  source: { id: string; type: string } | null;
}

export interface SourceAccountMessage {
  id: string;
  collectionId: string;
  collectionTitle: string;
  senderName: string;
  sentAt: string;
  text: string;
  hasMedia: boolean;
  media: MessageMedia | null;
}

export interface SourceAccountMessagesPage {
  items: SourceAccountMessage[];
  nextCursor: string | null;
}

export const sourceAccountApi = {
  get: (connector: string, id: string) =>
    apiFetch<SourceAccount>(
      `/api/connectors/accounts/${encodeURIComponent(connector)}/${encodeURIComponent(id)}`,
    ),
  listMessages: (connector: string, id: string, opts?: { limit?: number; before?: string }) => {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts?.before) params.set('before', opts.before);
    const qs = params.toString();
    return apiFetch<SourceAccountMessagesPage>(
      `/api/connectors/accounts/${encodeURIComponent(connector)}/${encodeURIComponent(id)}/messages${qs ? `?${qs}` : ''}`,
    );
  },
};

// re-export for convenience
export { apiFetch, apiUpload };

// ---------------------------------------------------------------------------
// Destinations API (plan 2026-08-04-destinations-v1.md §5.5)
// ---------------------------------------------------------------------------

export interface DestinationSchedule {
  tz: string;
  runs: { at: string }[];
}

/** The public destination view — the webhook URL is never echoed (plan D5). */
export interface Destination {
  id: string;
  type: 'webhook';
  format: 'slack';
  label: string | null;
  host: string | null;
  schedule: DestinationSchedule | null;
  postSummary: boolean;
  status: 'active' | 'paused' | 'error' | 'disconnected';
  lastError: string | null;
  lastRunAt: string | null;
  createdAt: string;
  lastRun?: DestinationRun | null;
}

export interface DestinationRun {
  id: string;
  status: 'running' | 'succeeded' | 'failed';
  trigger?: 'schedule' | 'manual';
  dryRun: boolean;
  windowStart?: string;
  windowEnd?: string;
  stats: Record<string, unknown>;
  degradations: string[];
  error?: string | null;
  startedAt: string;
  finishedAt?: string | null;
}

export interface DestinationRecord {
  id: string;
  kind: 'task';
  status: 'candidate' | 'delivered' | 'suppressed' | 'merged';
  suppressedReason: string | null;
  mergedIntoId: string | null;
  payload: {
    title: string;
    body_md: string;
    client_hint: string | null;
    owner_hint: string | null;
    due: string | null;
    due_hint: string | null;
    priority: 'low' | 'medium' | 'high' | null;
    confidence: number;
    rendered?: unknown;
  };
  provenance: { message_ids: string[]; collection_ids: string[]; segment_id: string };
  createdAt: string;
}

export interface DestinationRunDetail extends DestinationRun {
  records: DestinationRecord[];
}

export const destinationsApi = {
  list: () => apiFetch<Destination[]>('/api/destinations'),
  get: (id: string) => apiFetch<Destination>(`/api/destinations/${id}`),
  connect: (input: {
    url: string;
    format: 'slack';
    label?: string;
    schedule: DestinationSchedule;
    postSummary: boolean;
  }) =>
    apiFetch<Destination & { probe: string; warning: string | null }>('/api/destinations', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  patch: (
    id: string,
    patch: {
      label?: string | null;
      schedule?: DestinationSchedule;
      postSummary?: boolean;
      status?: 'active' | 'paused';
    },
  ) =>
    apiFetch<Destination>(`/api/destinations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  disconnect: (id: string) =>
    apiFetch<{ disconnected: boolean }>(`/api/destinations/${id}`, { method: 'DELETE' }),
  sendTest: (id: string) =>
    apiFetch<{ probe: string }>(`/api/destinations/${id}/test`, { method: 'POST' }),
  listRuns: (id: string) => apiFetch<DestinationRun[]>(`/api/destinations/${id}/runs`),
  /** `dryRun` defaults to true server-side — a real run must ask explicitly. */
  createRun: (id: string, opts?: { windowStart?: string; windowEnd?: string; dryRun?: boolean }) =>
    apiFetch<DestinationRun>(`/api/destinations/${id}/runs`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    }),
  getRun: (id: string, runId: string) =>
    apiFetch<DestinationRunDetail>(`/api/destinations/${id}/runs/${runId}`),
};
